import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../shared/api/AllApi";

const CACHE_TTL_MS = 5 * 60 * 1000;

const normalizeDefault = (payload) => (Array.isArray(payload) ? payload : []);

const normalizeAccountants = (payload) => {
  if (payload?.success && Array.isArray(payload.data)) {
    return payload.data;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === "object" && payload.accountants) {
    return Object.entries(payload.accountants).map(([id, value]) => ({
      id,
      ...value,
    }));
  }

  if (payload && typeof payload === "object") {
    return Object.entries(payload).map(([id, value]) => ({ id, ...value }));
  }

  return [];
};

const CATALOG_CONFIG = {
  products: { endpoint: "/products", normalize: normalizeDefault },
  units: { endpoint: "/units", normalize: normalizeDefault },
  categories: { endpoint: "/categories", normalize: normalizeDefault },
  customers: { endpoint: "/customers", normalize: normalizeDefault },
  accountants: { endpoint: "/accountants", normalize: normalizeAccountants },
};

const catalogStore = {
  products: { data: null, fetchedAt: 0, promise: null },
  units: { data: null, fetchedAt: 0, promise: null },
  categories: { data: null, fetchedAt: 0, promise: null },
  customers: { data: null, fetchedAt: 0, promise: null },
  accountants: { data: null, fetchedAt: 0, promise: null },
};

const isFresh = (entry) =>
  Boolean(entry.data) && Date.now() - entry.fetchedAt < CACHE_TTL_MS;

const fetchCatalog = async (key, force = false) => {
  const config = CATALOG_CONFIG[key];
  const entry = catalogStore[key];
  const endpoint = config?.endpoint;
  const normalize = config?.normalize || normalizeDefault;

  if (!endpoint || !entry) {
    throw new Error(`Unsupported catalog key: ${key}`);
  }

  if (!force && isFresh(entry)) {
    return entry.data;
  }

  if (!force && entry.promise) {
    return entry.promise;
  }

  entry.promise = apiClient
    .get(endpoint)
    .then((response) => {
      entry.data = normalize(response.data);
      entry.fetchedAt = Date.now();
      return entry.data;
    })
    .finally(() => {
      entry.promise = null;
    });

  return entry.promise;
};

export const invalidateCatalog = (key) => {
  if (key && catalogStore[key]) {
    catalogStore[key] = { data: null, fetchedAt: 0, promise: null };
    return;
  }

  Object.keys(catalogStore).forEach((catalogKey) => {
    catalogStore[catalogKey] = { data: null, fetchedAt: 0, promise: null };
  });
};

export const useCatalogData = (key) => {
  const entry = catalogStore[key];
  const [data, setData] = useState(entry?.data || []);
  const [loading, setLoading] = useState(!entry?.data);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (force = false) => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchCatalog(key, force);
        setData(result || []);
        return result;
      } catch (err) {
        setError(err);
        setData([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [key]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);

  return { data, loading, error, refresh };
};

export const useProductsCatalog = () => useCatalogData("products");
export const useUnitsCatalog = () => useCatalogData("units");
export const useCategoriesCatalog = () => useCatalogData("categories");
export const useCustomersCatalog = () => useCatalogData("customers");
export const useAccountantsCatalog = () => useCatalogData("accountants");

